import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Time } from '@/components/ui/time';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EnumProjectAction, SearchProjectAuditLogQuery } from '@/lib/api/sites/audit-log';
import { useQuery } from '@tanstack/react-query';

const getActionDisplay = (action?: EnumProjectAction) => {
  if (!action) return '';
  switch (action) {
    case EnumProjectAction.Viewed:
      return 'Viewed';
    case EnumProjectAction.ContactAdded:
      return 'Contact Added';
    case EnumProjectAction.ArmStateChanged:
      return 'Arm State Changed';
    case EnumProjectAction.ArmProfileChanged:
      return 'Arm Profile Changed';
    case EnumProjectAction.SirenProfileChanged:
      return 'Siren Profile Changed';
    case EnumProjectAction.NoThreatDispositionChanged:
      return 'No Threat Disposition Changed';
    case EnumProjectAction.MonitoringZoneUpdated:
      return 'Monitoring Zone Updated';
    case EnumProjectAction.NameChanged:
      return 'Name Changed';
    case EnumProjectAction.CompanyChanged:
      return 'Company Changed';
    case EnumProjectAction.NewMonitoringAlertsAfterSecondsChanged:
      return 'New Alert Threshold';
    case EnumProjectAction.SirenCooldownSecondsChanged:
      return 'Siren Cooldown Seconds Changed';
    case EnumProjectAction.SirensUnlinkedChanged:
      return 'Sirens Unlinked Changed';
    case EnumProjectAction.GuardResponseEnabledChanged:
      return 'Guard Response Enabled Changed';
    case EnumProjectAction.GuardResponseDispatchCodeChanged:
      return 'Guard Response Dispatch Code Changed';
    case EnumProjectAction.ComplianceEnabledChanged:
      return 'Compliance Enabled Changed';
    case EnumProjectAction.SiteNotesChanged:
      return 'Site Notes Changed';
    case EnumProjectAction.AiAutomationLevelChanged:
      return 'AI Automation Level Changed';
    case EnumProjectAction.AiPrimerChanged:
      return 'AI Threat criteria Changed';
    case EnumProjectAction.NoThreatAiPrimerChanged:
      return 'No Threat AI criteria Changed';
    case EnumProjectAction.TimeZoneChanged:
      return 'Time Zone Changed';
    case EnumProjectAction.AddressChanged:
      return 'Address Changed';
    case EnumProjectAction.DetectionLabelsChanged:
      return 'Detection Labels Changed';
    case EnumProjectAction.ExternalServiceOptionsChanged:
      return 'External Service Options Changed';
    case EnumProjectAction.ArchivedAtChanged:
      return 'Archived State Changed';
    case EnumProjectAction.GridChanged:
      return 'Grid Changed';
    default:
      return action;
  }
};

interface SiteLogProps {
  projectId: string;
}

export function SiteLog({ projectId }: SiteLogProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState<EnumProjectAction | undefined>(undefined);
  const pageSize = 100;

  const logsQuery = useQuery(
    SearchProjectAuditLogQuery({
      projectId,
      search: searchQuery,
      action: selectedAction,
      pageSize,
      pageNumber: Math.max(currentPage - 1, 0),
    })
  );
  const logs = logsQuery.data?.rows ?? [];
  const totalRows = logsQuery.data?.totalRows ?? 0;
  const totalPages = Math.ceil(totalRows / pageSize);
  const showPagination = totalPages > 1;

  const onNext = () => {
    const nextPage = Math.min(currentPage + 1, totalPages);
    setCurrentPage(nextPage);
  };

  const onPrevious = () => {
    const prevPage = Math.max(currentPage - 1, 1);
    setCurrentPage(prevPage);
  };

  return (
    <div className="space-y-4">
      <div className="mx-auto ">
        <div className="">
        
          <div className="flex gap-4 p-4 border m-4 rounded-xl bg-card1">
            <Input
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-11 max-w-[500px] text-base"
              isSearch
            />
            <Select
              value={selectedAction}
              onValueChange={(value) =>
                setSelectedAction(value === 'all' ? undefined : (value as EnumProjectAction))
              }
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {Object.values(EnumProjectAction).map((action) => (
                  <SelectItem key={action} value={action}>
                    {getActionDisplay(action)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="relative min-h-[200px]">
        {logsQuery.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-600 border-t-blue-500"></div>
              <p className="text-gray-400">Loading...</p>
            </div>
          </div>
        )}

        {logs.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center text-gray-400">
              <p>No audit logs found</p>
            </div>
          </div>
        ) : (
          <div className="m-4 border rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="text-white">
                {logs.map((log) => (
                  <TableRow key={log.occurred?.toString()}>
                    <TableCell className="font-medium">{getActionDisplay(log.action)}</TableCell>
                    <TableCell className="text-gray-400">
                      <Time date={log.occurred} />
                    </TableCell>
                    <TableCell className="text-gray-400">{log.userName}</TableCell>
                    <TableCell className="text-gray-400">{log.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {showPagination && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-gray-400">Total logs: {totalRows}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onPrevious} disabled={currentPage === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onNext}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
